import type { Overlap } from '../types';

const SEEN_KEY = 'seen_overlaps_';
const MUTED_KEY = 'muted_friends_';

const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days
const MAX_SEEN = 200;

function getItem<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function setItem(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore quota errors */ }
}

// ── Fingerprint-based overlap dedup ─────────────────────────────

interface SeenEntry {
  fp: string;
  ts: number;
}

export function computeOverlapFingerprint(overlap: Overlap): string {
  const sortedFriendIds = overlap.friends
    .map((f) => f.user.id)
    .sort()
    .join(',');
  return `${overlap.city}:${overlap.startDate}:${sortedFriendIds}`;
}

export function getSeenOverlaps(userId: string): Set<string> {
  const raw = getItem<SeenEntry[] | string[]>(SEEN_KEY + userId);
  if (!raw || raw.length === 0) return new Set();

  const now = Date.now();

  // Auto-migrate old string[] format → SeenEntry[]
  if (typeof raw[0] === 'string') {
    const migrated: SeenEntry[] = (raw as string[]).map((fp) => ({ fp, ts: now }));
    setItem(SEEN_KEY + userId, migrated);
    return new Set(migrated.map((e) => e.fp));
  }

  // Prune expired entries and enforce hard cap
  let entries = (raw as SeenEntry[]).filter((e) => now - e.ts < TTL_MS);
  if (entries.length > MAX_SEEN) {
    entries.sort((a, b) => b.ts - a.ts);
    entries = entries.slice(0, MAX_SEEN);
  }
  if (entries.length !== (raw as SeenEntry[]).length) {
    setItem(SEEN_KEY + userId, entries);
  }
  return new Set(entries.map((e) => e.fp));
}

export function markOverlapsSeen(userId: string, fingerprints: string[]): void {
  const raw = getItem<SeenEntry[]>(SEEN_KEY + userId) ?? [];
  const now = Date.now();

  // Migrate if needed
  const entries: SeenEntry[] = raw.length > 0 && typeof (raw as unknown[])[0] === 'string'
    ? (raw as unknown as string[]).map((fp) => ({ fp, ts: now }))
    : (raw as SeenEntry[]).filter((e) => now - e.ts < TTL_MS);

  const existing = new Set(entries.map((e) => e.fp));
  for (const fp of fingerprints) {
    if (!existing.has(fp)) {
      entries.push({ fp, ts: now });
    }
  }
  // Enforce hard cap — keep newest entries
  if (entries.length > MAX_SEEN) {
    entries.sort((a, b) => b.ts - a.ts);
    entries.length = MAX_SEEN;
  }
  setItem(SEEN_KEY + userId, entries);
}

// ── Muted friends ───────────────────────────────────────────────

export function getMutedFriends(userId: string): Set<string> {
  const stored = getItem<string[]>(MUTED_KEY + userId);
  return new Set(stored ?? []);
}

export function toggleFriendMute(userId: string, friendId: string): boolean {
  const current = getMutedFriends(userId);
  const newMuted = !current.has(friendId);
  if (newMuted) {
    current.add(friendId);
  } else {
    current.delete(friendId);
  }
  setItem(MUTED_KEY + userId, [...current]);
  return newMuted;
}

export function isFriendMuted(userId: string, friendId: string): boolean {
  return getMutedFriends(userId).has(friendId);
}
